// Shared money grammar for source text, clarifications, and model arguments.
export const AMOUNT_SUFFIX_SOURCE = String.raw`(?:тыс\.?|тысяч(?:а|и|у)?|thousands?|[кk]|млн|mln|millions?|миллион(?:а|ов)?|m)`;
const currencySource = String.raw`(?:₽|руб(?:л(?:ь|я|ей)?)?\.?|(?:rub|roubles?|rubles?)\.?)`;
const numericSource = String.raw`\d+(?:[ \u00a0\u202f]\d+)*(?:[.,]\d+)*`;
// Failed suffixes/groups must never fall back to a shorter numeric prefix.
export const AMOUNT_TEXT_SOURCE = String.raw`(?:${numericSource}(?:\s*${AMOUNT_SUFFIX_SOURCE})?(?:\s*${currencySource})?|бесплатно|free)(?![\p{L}\p{N}_.,]|[ \u00a0\u202f]\d)`;

export const AMOUNT_SCALE_SUFFIX_SOURCE = String.raw`${AMOUNT_SUFFIX_SOURCE}(?![\p{L}\p{N}_])`;

/** Returns 1 000 / 1 000 000 for a recognized scale suffix, 1 for none, undefined for unknown. */
export function amountScaleFactor(suffix: string | undefined): number | undefined {
  if (suffix === undefined) return 1;
  const normalized = suffix.normalize('NFKC').trim().toLocaleLowerCase('ru-RU').replace(/\.$/u, '');
  if (normalized === '') return 1;
  if (
    ['к', 'k', 'тыс', 'тысяча', 'тысячи', 'тысяч', 'тысячу', 'thousand', 'thousands'].includes(
      normalized,
    )
  ) {
    return 1_000;
  }
  if (
    ['mln', 'млн', 'миллион', 'миллиона', 'миллионов', 'm', 'million', 'millions'].includes(
      normalized,
    )
  ) {
    return 1_000_000;
  }
  return undefined;
}

interface ExactDecimal {
  readonly sign: 1 | -1;
  readonly coefficient: bigint;
  readonly scale: number;
}

function exactDecimal(value: string): ExactDecimal | undefined {
  const match = /^(-?)(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/u.exec(value);
  if (match === null) return undefined;
  const exponent = match[4] === undefined ? 0 : Number(match[4]);
  if (!Number.isSafeInteger(exponent)) return undefined;
  const digits = `${match[2]}${match[3] ?? ''}`.replace(/^0+/u, '');
  if (digits === '') return { sign: 1, coefficient: 0n, scale: 0 };
  let coefficient: bigint;
  try {
    coefficient = BigInt(digits);
  } catch {
    return undefined;
  }
  let scale = (match[3]?.length ?? 0) - exponent;
  while (coefficient % 10n === 0n) {
    coefficient /= 10n;
    scale -= 1;
  }
  return { sign: match[1] === '-' ? -1 : 1, coefficient, scale };
}

function hasExactNumericRoundTrip(rawValue: string, value: number): boolean {
  const source = exactDecimal(rawValue);
  const roundTripped = exactDecimal(value.toString());
  return (
    source !== undefined &&
    roundTripped !== undefined &&
    source.sign === roundTripped.sign &&
    source.coefficient === roundTripped.coefficient &&
    source.scale === roundTripped.scale
  );
}

function decimalToString(value: ExactDecimal): string {
  if (value.coefficient === 0n) return '0';
  const sign = value.sign === -1 ? '-' : '';
  const digits = value.coefficient.toString();
  if (value.scale <= 0) return `${sign}${digits}${'0'.repeat(-value.scale)}`;
  if (value.scale >= digits.length)
    return `${sign}0.${'0'.repeat(value.scale - digits.length)}${digits}`;
  const integerEnd = digits.length - value.scale;
  return `${sign}${digits.slice(0, integerEnd)}.${digits.slice(integerEnd)}`;
}

export function finiteSafeNumber(value: string): number | undefined {
  const number = Number(value);
  if (
    !Number.isFinite(number) ||
    Math.abs(number) > Number.MAX_SAFE_INTEGER ||
    !hasExactNumericRoundTrip(value, number)
  ) {
    return undefined;
  }
  return number;
}

/** Strict grouping and exact decimal scaling without floating-point guesses. */
export function parseAmountText(raw: string): number | undefined {
  const text = raw.trim();
  if (/^(?:бесплатно|free)$/iu.test(text)) return 0;
  const match = new RegExp(
    String.raw`^(${numericSource})(?:\s*(${AMOUNT_SUFFIX_SOURCE}))?(?:\s*${currencySource})?$`,
    'iu',
  ).exec(text);
  if (match === null) return undefined;
  const source = match[1]!;
  const compact = source.replace(/[ \u00a0\u202f]/gu, '');
  const dot = compact.lastIndexOf('.');
  const comma = compact.lastIndexOf(',');
  let integer = source;
  let fraction = '';
  let grouping: string | undefined;
  if (dot >= 0 && comma >= 0) {
    const decimal = dot > comma ? '.' : ',';
    grouping = decimal === '.' ? ',' : '.';
    const parts = source.split(decimal);
    if (parts.length !== 2) return undefined;
    [integer, fraction] = parts as [string, string];
    if (!/^\d{1,2}$/u.test(fraction)) return undefined;
  } else if (dot >= 0 || comma >= 0) {
    const separator = comma >= 0 ? ',' : '.';
    const parts = source.split(separator);
    if (parts.slice(1).every((part) => /^\d{3}$/u.test(part))) grouping = separator;
    else {
      if (parts.length !== 2 || !/^\d{1,2}$/u.test(parts[1]!)) return undefined;
      [integer, fraction] = parts as [string, string];
    }
  }
  const hasSpaces = /[ \u00a0\u202f]/u.test(integer);
  if (grouping !== undefined && hasSpaces) return undefined;
  if (grouping !== undefined || hasSpaces) {
    const separator = grouping === undefined ? '[ \\u00a0\\u202f]' : grouping === '.' ? '\\.' : ',';
    if (!new RegExp(String.raw`^\d{1,3}(?:${separator}\d{3})+$`, 'u').test(integer))
      return undefined;
  } else if (!/^\d+$/u.test(integer)) return undefined;
  const normalized = integer.replace(/[ .,\u00a0\u202f]/gu, '') + (fraction ? `.${fraction}` : '');
  const decimal = exactDecimal(normalized);
  if (decimal === undefined) return undefined;
  const suffix = match[2];
  const scale =
    suffix === undefined ? 0 : /^(?:млн|mln|m|миллион(?:а|ов)?|millions?)$/iu.test(suffix) ? 6 : 3;
  return finiteSafeNumber(decimalToString({ ...decimal, scale: decimal.scale - scale }));
}

/** Multiple stated amounts are ambiguous; no first-match guess is permitted. */
export function parseExplicitAmountText(input: string): number | undefined {
  return explicitAmountFact(input)?.value;
}

/** Preserve the literal evidence for deterministic envelope builders. */
export function explicitAmountFact(
  input: string,
  maxAmount = 9_999_999_999.99,
): { value: number; source: string } | undefined {
  const markers = input.match(
    /(?<![\p{L}\p{N}_])(?:сумм(?:а|у|ой)?|стоимост(?:ь|ью)?|amount|value|на|for)\s*[:=]?\s*(?=\d|бесплатно|free)/giu,
  );
  if ((markers?.length ?? 0) > 1) return undefined;
  const pattern = new RegExp(
    String.raw`(?<![\p{L}\p{N}_])(?:(?:сумм(?:а|у|ой)?|стоимост(?:ь|ью)?|amount|value|на|for)\s*[:=]?\s*(${AMOUNT_TEXT_SOURCE})|((?:бесплатно|free)(?![\p{L}\p{N}_])))`,
    'giu',
  );
  const matches = [...input.matchAll(pattern)];
  if (matches.length !== 1) return undefined;
  const match = matches[0]!;
  const rest = input.slice((match.index ?? 0) + match[0].length);
  if (/^\s*(?:[-–—]|или|or)\s*\d/iu.test(rest)) return undefined;
  const amount = parseAmountText(match[1] ?? match[2] ?? '');
  return amount !== undefined && amount <= maxAmount
    ? { value: amount, source: match[1] ?? match[2] ?? '' }
    : undefined;
}

/** Compatibility entry point using the shared strict money grammar. */
export function parseScaledAmountText(rawNumber: string, scaleSuffix?: string): number | undefined {
  const factor = amountScaleFactor(scaleSuffix);
  if (factor === undefined) return undefined;
  return parseAmountText(
    `${rawNumber.trim()}${factor === 1000 ? 'k' : factor === 1_000_000 ? 'm' : ''}`,
  );
}
